import { Component, Input, OnInit } from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { AlertController, ModalController, ViewWillLeave } from '@ionic/angular'
import { PlanType } from 'formwork-planner-lib'
import { Subject } from 'rxjs'
import { Part } from '../../../../../models/part'
import { Plan } from '../../../../../models/plan'
import { Screenshot } from '../../../../../models/screenshot'
import { PlanResultRepository } from '../../../../../repositories/plan-result.repository'
import { AppBackgroundService } from '../../../../../services/app-background.service'
import { FileService } from '../../../../../services/file.service'
import { LoadingSpinnerService } from '../../../../../services/loading-spinner.service'
import { PlanService } from '../../../../../services/plan.service'
import { ScreenshotService } from '../../../../../services/screenshot.service'
import { Translation } from '../../../../../services/translation.service'
import { PdfWorkerRequest } from '../../../../../worker/resultPdfWorkerData'
import { ResultMessageService } from '../../../services/result-message.service'
import {
  ApplicationInsightsService,
  ApplicationInsightsStates,
} from '../../../../../services/applicationInsights.service'
import { PlanSettings } from '../../../../../models/planSettings'
import { PlanSettingsService } from '../../../../../services/plan-settings.service'
import { MeasurementLegendService } from '../../../../../services/measurement-legend.service'

@Component({
  selector: 'efp-pdf-export',
  templateUrl: './pdf-export.component.html',
  styleUrls: ['./pdf-export.component.scss'],
})
export class PdfExportComponent implements OnInit, ViewWillLeave {
  @Input() screens!: Screenshot[]
  @Input() planId!: number

  public includeTotalStock = true
  public includeCycleStock = true
  private readonly pageLeave$ = new Subject<void>()
  private plan?: Plan
  private planSettings?: PlanSettings
  private buildingType?: PlanType
  public showStockPerCycle = false

  get hasIncludedScreens(): boolean {
    return this.screens.some((screen) => screen.useInPdf)
  }

  constructor(
    private readonly modalCtrl: ModalController,
    private readonly alertCtrl: AlertController,
    private readonly translate: Translation,
    private readonly loadingSpinner: LoadingSpinnerService,
    private readonly fileService: FileService,
    private readonly messageService: ResultMessageService,
    private readonly screenshotService: ScreenshotService,
    private readonly activeRoute: ActivatedRoute,
    private readonly planService: PlanService,
    private readonly planResultRepository: PlanResultRepository,
    private readonly appBackgroundService: AppBackgroundService,
    private readonly appInsightsService: ApplicationInsightsService,
    private readonly planSettingsService: PlanSettingsService,
    private readonly measurementLegendService: MeasurementLegendService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.planId) {
      this.plan = await this.planService.findOne(this.planId)
      this.planSettings = await this.planSettingsService.getPlanSettingsAndSetLastUnit(
        this.plan.settingsId
      )
      this.buildingType = this.plan.buildingType
      this.showStockPerCycle = this.plan.buildingType === PlanType.WALL

      if (this.screens.length === 0 && this.planId) {
        this.screens = await this.screenshotService.loadData(this.plan)
        this.screens.forEach((screen) => (screen.useInPdf = true))
      } else {
        this.screens.forEach((screen) => (screen.useInPdf = true))
      }
    }
  }

  async closeModal(): Promise<void> {
    await this.modalCtrl.dismiss()
  }

  public async exportPDF(): Promise<void> {
    if (!this.plan) {
      throw new Error('PdfExportComponent.exportPDF - Plan not found')
    }

    const alert = await this.alertCtrl.create({
      cssClass: ['alertStyle', 'alertStyleTwoButtons'],
      header: this.translate.translate('SCREENSHOT.SET_NAME'),
      inputs: [
        {
          type: 'text',
          name: 'name',
          placeholder: this.plan.name,
          value: this.plan.name,
          attributes: { required: true },
        },
      ],
      buttons: [
        {
          text: this.translate.translate('GENERAL.CANCEL'),
        },
        {
          text: this.translate.translate('GENERAL.OK'),
          handler: async (data) =>
            await this.generatePDF(data.name ? data.name + '.pdf' : 'export.pdf'),
        },
      ],
    })
    await alert.present()
  }

  private async generatePDF(name: string): Promise<void> {
    return this.loadingSpinner.doWithLoadingSpinner(async () => {
      try {
        const screens = this.screens.filter((x) => x.useInPdf)
        const logMessages = this.messageService.messagesForPDF.map((message) =>
          this.translate.translate(message.message ? message.message : '')
        )
        let resultStock: undefined | Part[] = undefined
        if (this.includeTotalStock || this.includeCycleStock) {
          resultStock = (
            await this.planResultRepository.getArticleListWithCycleUsageForPlan(this.planId)
          )?.parts
        }
        const translations: PdfWorkerRequest['translations'] = {
          'PDF.AMOUNT': this.translate.translate('PDF.AMOUNT'),
          'PDF.ARTICLE_NR': this.translate.translate('PDF.ARTICLE_NR'),
          'PDF.CREATED_WITH': this.translate.translate('PDF.CREATED_WITH'),
          'PDF.DESCRIPTION': this.translate.translate('PDF.DESCRIPTION'),
          'PDF.ERROR_CALCULATION': this.translate.translate('PDF.ERROR_CALCULATION'),
          'PDF.PAGE': this.translate.translate('PDF.PAGE'),
          'PDF.TOTAL_AMOUNT': this.translate.translate('PDF.TOTAL_AMOUNT'),
          'VIEWER.CYCLE': this.translate.translate('VIEWER.CYCLE'),
        }

        if (this.plan && this.planSettings) {
          const pdfData: PdfWorkerRequest = {
            isPdfWorkerRequest: true,
            screens,
            logMessages,
            includeTotalStock: this.includeTotalStock,
            includeCycleStock: this.includeCycleStock,
            resultStock,
            translations,
            buildingType: this.plan.buildingType.toString(),
            measurementUnit: this.planSettings.measurementUnit,
            slabThicknes: this.measurementLegendService.convertValue(
              this.planSettings.measurementUnit,
              this.planSettings.slabThickness
            ),
            slabTopLevel: this.measurementLegendService.convertValue(
              this.planSettings.measurementUnit,
              this.planSettings.slabHeight
            ),
            wallHeight: this.measurementLegendService.convertValue(
              this.planSettings.measurementUnit,
              this.planSettings.wallHeight
            ),
            articleMeasurementUnit: this.measurementLegendService.getMeasurementUnitArticle(
              this.planSettings.formworkWall
            ),
          }
          const pdfResult = await this.appBackgroundService.generateResultPdfInBackground(pdfData)
          await this.fileService.shareOrDownloadBlobFile(pdfResult.pdfBlob, `PDF/${name}`, name)

          this.appInsightsService.addUserEvent(ApplicationInsightsStates.PDF_CREATED, this.planId)

          await this.closeModal()
        }
      } catch (err: unknown) {
        console.error(err)

        const alert = await this.alertCtrl.create({
          backdropDismiss: false,
          header: this.translate.translate('PDF.ERROR_HEADLINE'),
          message: this.translate.translate('PDF.ERROR_MESSAGE'),
          buttons: [
            {
              text: this.translate.translate('GENERAL.CANCEL'),
            },
            {
              text: this.translate.translate('GENERAL.OK'),
              handler: async () => await this.generatePDF(name),
            },
          ],
        })
        await alert.present()
      }
    })
  }

  ionViewWillLeave(): void {
    this.pageLeave$.next()
  }
}
